import useWith from 'ramda/es/useWith';
import equals from 'ramda/es/equals';
import identity from 'ramda/es/identity';
import length from 'ramda/es/length';
import o from 'ramda/es/o';
import between from 'ramda-extension/es/between';
import gte from 'ramda/es/gte';
import lte from 'ramda/es/lte';
import test from 'ramda/es/test';
import curry from 'ramda/es/curry';
import flip from 'ramda/es/flip';
import modulo from 'ramda/es/modulo';
import compose from 'ramda/es/compose';
import equalsToZero from 'ramda-extension/es/equalsToZero';
import both from 'ramda/es/both';
import either from 'ramda/es/either';
import isNilOrEmptyString from 'ramda-extension/es/isNilOrEmptyString';
import isNumeric from 'ramda-extension/es/isNumeric';
import anyPass from 'ramda/es/anyPass';
import isPositive from 'ramda-extension/es/isPositive';
import T from 'ramda/es/T';
import isTruthy from 'ramda-extension/es/isTruthy';
import replace from 'ramda/es/replace';
import splitAt from 'ramda/es/splitAt';
import reverse from 'ramda/es/reverse';
import pipe from 'ramda/es/pipe';
import map from 'ramda/es/map';
import when from 'ramda/es/when';
import __ from 'ramda/es/__';
import subtract from 'ramda/es/subtract';
import slice from 'ramda/es/slice';
import toString from 'ramda/es/toString';
import concat from 'ramda/es/concat';
import ifElse from 'ramda/es/ifElse';
import drop from 'ramda/es/drop';
import converge from 'ramda/es/converge';
import invoker from 'ramda/es/invoker';
import isNotNumeric from 'ramda-extension/es/isNotNumeric';
import ltThanLength from 'ramda-extension/es/ltThanLength';
import listToString from 'ramda-extension/es/listToString';
import startsWith$1 from 'ramda/es/startsWith';

/**
@alias module:predicates.hasLength
*/

var hasLength = useWith(equals, [identity, length]);

/**
@alias module:predicates.hasLengthInterval
*/

var hasLengthInInterval = (function (min, max) {
  return o(between(min, max), length);
});

/**
@alias module:predicates.hasLengthMax
*/

var hasLengthMax = useWith(gte, [identity, length]);

/**
@alias module:predicates.hasLengthMin
*/

var hasLengthMin = useWith(lte, [identity, length]);

/**
@alias module:predicates.hasNoSpecialSymbols
*/

var hasNoSpecialSymbols = test(/^[^_~`!#$%\^&*+=\-\[\]\\;,.\/{}@´'§|\\":<>\?]*$/g);

/**
@alias module:predicates.hasOnlyDigits
*/

var hasOnlyDigits = test(/^\d*$/);

var isDivisible = compose(equalsToZero, modulo);
/**
@alias module:predicates.isDivisibleBy
*/

var isDivisibleBy = curry(flip(isDivisible));

var emailRegExp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
/**
@alias module:predicates.isEmail
*/

var isEmail = either(isNilOrEmptyString, both(test(emailRegExp), hasLengthMax(200)));

/**
@alias module:predicates.isInteger
*/

var isInteger = test(/^-?[0-9]+$/);

/**
@alias module:predicates.isPhoneNumber
*/

var isPhoneNumber = anyPass([isNilOrEmptyString, test(/^\d{9}$/)]);

/**
@alias module:predicates.isRequiredNumber
*/

var isRequiredNumber = anyPass([equalsToZero, isTruthy]);

var minus55 = subtract(__, 55);
var decodeChar = o(minus55, invoker(0, 'charCodeAt'));
var removeWhiteSpaces = replace(/ /g, '');
var modulo97 = o(modulo(__, 97), parseInt);
var take9chars = slice(0, 9);
var drop9charsFromIban = converge(drop, [o(length, take9chars), identity]);
var moduloOfFirst9Chars = compose(toString, modulo97, take9chars);
var computeReducedIban = converge(concat, [moduloOfFirst9Chars, drop9charsFromIban]);
var moveFirstFourCharsToEnd = compose(listToString, reverse, splitAt(4)); // on wikipedia they are using hardcoded values, but the value of number can be computed with ascii code - 55

var expandTextCharToNumber = o(listToString, map(when(isNotNumeric, decodeChar))); // javascript is not capable to compute module on whole number
// https://en.wikipedia.org/wiki/International_Bank_Account_Number#Modulo_operation_on_IBAN

var iso7064Mod97 = ifElse(ltThanLength(2), o(function (c) {
  return iso7064Mod97(c);
}, computeReducedIban), modulo97); // based on https://en.wikipedia.org/wiki/International_Bank_Account_Number#Validating_the_IBAN

/**
@alias module:predicates.isValidIban
*/

var isValidIban = pipe(removeWhiteSpaces, moveFirstFourCharsToEnd, expandTextCharToNumber, iso7064Mod97, equals(1));

/**
@alias module:predicates.startsWith
*/

var startsWith = (function (string) {
  return startsWith$1(string);
});

/**
@alias module:predicates.hasNoWhiteSpace
*/

var hasNoWhiteSpace = test(/^[\S]*$/g);

/**
@alias module:predicates.isTrimmed
*/

var isTrimmed = test(/^\S([\S\s]*?)\S$|^\S*$/);

var isTrimmedLeft = test(/^\S|^\S*$/);

var isTrimmedRight = test(/\S$|^\S*$/);

export { hasLength, hasLengthInInterval, hasLengthMax, hasLengthMin, hasNoSpecialSymbols, hasNoWhiteSpace, hasOnlyDigits, between as hasValueInInterval, gte as hasValueMax, lte as hasValueMin, isDivisibleBy, isEmail, isInteger, isNumeric as isNumber, isPhoneNumber, isPositive as isPositiveNumber, T as isRequired, isRequiredNumber, isTrimmed, isTrimmedLeft, isTrimmedRight, isValidIban, startsWith };
